import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Alert, Snackbar,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, InputAdornment, Tabs, Tab,
  Select, MenuItem, FormControl, InputLabel, List, ListItem,
  ListItemText, ListItemIcon, Divider, Popover
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import googleSheetsService from '../../services/googleSheetsService';
import CronBuilder from '../scheduled-jobs/CronBuilder';

export default function GoogleSheetsManager() {
  // === State ===
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(null); // null=checking, true/false
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(0); // 0=browse, 1=select tab, 2=preview schema, 3=importing
  const [inputMode, setInputMode] = useState(0); // 0=search, 1=paste URL

  // Browse state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Selected sheet
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [loadingTabs, setLoadingTabs] = useState(false);

  // Schema preview
  const [schema, setSchema] = useState(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [tableName, setTableName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);

  // Refresh/delete
  const [refreshingId, setRefreshingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Mapping dialog
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingImport, setMappingImport] = useState(null);
  const [mappingSuggestions, setMappingSuggestions] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingConfirming, setMappingConfirming] = useState(false);

  // Mode toggle
  const [updatingMode, setUpdatingMode] = useState(null); // registryId being toggled

  // Schedule popover
  const [scheduleAnchor, setScheduleAnchor] = useState(null);
  const [scheduleImport, setScheduleImport] = useState(null);
  const [cronExpression, setCronExpression] = useState('0 2 * * *');
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // === Load data ===
  const loadImports = useCallback(async () => {
    try {
      setLoading(true);
      const [importRes, statusRes] = await Promise.allSettled([
        googleSheetsService.getImports(),
        googleSheetsService.getConnectionStatus(),
      ]);

      if (importRes.status === 'fulfilled' && importRes.value.success) {
        setImports(importRes.value.imports || []);
      }
      if (statusRes.status === 'fulfilled') {
        setConnected(statusRes.value.connected || false);
      } else {
        setConnected(false);
      }
    } catch (err) {
      notify('Failed to load imports', 'error');
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadImports(); }, [loadImports]);

  const notify = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // === Browse ===
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      setSearching(true);
      const res = await googleSheetsService.browse(searchQuery, 'company');
      setSearchResults(res.files || []);
      if (!res.files?.length) notify('No spreadsheets found', 'info');
    } catch (err) {
      notify(err.response?.data?.error || 'Search failed', 'error');
    } finally {
      setSearching(false);
    }
  };

  const handlePasteUrl = async (url) => {
    if (!url.includes('spreadsheets/d/')) return;
    try {
      setSearching(true);
      const res = await googleSheetsService.parseUrl(url, 'company');
      if (res.success && res.spreadsheetId) {
        setSelectedSheet({
          spreadsheetId: res.spreadsheetId,
          name: res.title || 'Untitled',
          url,
        });
        // Load tabs
        await loadTabs(res.spreadsheetId);
        setImportStep(1);
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Invalid URL', 'error');
    } finally {
      setSearching(false);
    }
  };

  const selectSheet = async (sheet) => {
    setSelectedSheet(sheet);
    await loadTabs(sheet.spreadsheetId);
    setImportStep(1);
  };

  const loadTabs = async (spreadsheetId) => {
    try {
      setLoadingTabs(true);
      const res = await googleSheetsService.getTabs(spreadsheetId, 'company');
      setTabs(res.sheets || []);
    } catch (err) {
      notify('Failed to load tabs', 'error');
    } finally {
      setLoadingTabs(false);
    }
  };

  const selectTab = async (tab) => {
    setSelectedTab(tab);
    try {
      setLoadingSchema(true);
      setImportStep(2);
      const res = await googleSheetsService.previewSchema(
        selectedSheet.spreadsheetId, tab.title, headerRow, 'company'
      );
      if (res.success) {
        setSchema(res);
        setTableName(res.suggestedTableName || 'imported_sheet');
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Schema detection failed', 'error');
      setImportStep(1);
    } finally {
      setLoadingSchema(false);
    }
  };

  // === Import ===
  const handleImport = async () => {
    try {
      setImportStep(3);
      const res = await googleSheetsService.importSheet({
        spreadsheetId: selectedSheet.spreadsheetId,
        sheetName: selectedTab.title,
        tableName,
        headerRow,
      }, 'company');

      if (res.success) {
        notify(`Imported ${res.rows} rows into ${res.database}.${res.table}`);
        closeImportDialog();
        loadImports();
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Import failed';
      notify(msg, 'error');
      setImportStep(2); // Go back to schema preview
    }
  };

  // === Refresh ===
  const handleRefresh = async (registryId) => {
    try {
      setRefreshingId(registryId);
      const res = await googleSheetsService.refresh(registryId, 'company');
      if (res.success) {
        notify(`Refreshed: ${res.rows} rows`);
        loadImports();
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Refresh failed', 'error');
    } finally {
      setRefreshingId(null);
    }
  };

  // === Delete ===
  const handleDelete = async (registryId) => {
    try {
      const res = await googleSheetsService.deleteImport(registryId);
      if (res.success) {
        notify('Import removed');
        setDeleteConfirm(null);
        loadImports();
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  // === Mode toggle ===
  const handleToggleMode = async (imp, newMode) => {
    if (newMode === (imp.refresh_mode || 'full')) return; // No change
    try {
      setUpdatingMode(imp.id);
      await googleSheetsService.updateRefreshMode(imp.id, newMode);
      notify(`Refresh mode set to ${newMode}`);
      loadImports();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to update mode', 'error');
    } finally {
      setUpdatingMode(null);
    }
  };

  // === Schedule ===
  const openSchedulePopover = (event, imp) => {
    setScheduleAnchor(event.currentTarget);
    setScheduleImport(imp);
    setCronExpression(imp.cron_expression || '0 2 * * *');
  };

  const closeSchedulePopover = () => {
    setScheduleAnchor(null);
    setScheduleImport(null);
  };

  const handleSaveSchedule = async () => {
    try {
      setScheduleSaving(true);
      await googleSheetsService.updateSchedule(scheduleImport.id, cronExpression, true);
      notify('Schedule saved');
      closeSchedulePopover();
      loadImports();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to save schedule', 'error');
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleRemoveSchedule = async () => {
    try {
      setScheduleSaving(true);
      await googleSheetsService.removeSchedule(scheduleImport.id);
      notify('Schedule removed');
      closeSchedulePopover();
      loadImports();
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to remove schedule', 'error');
    } finally {
      setScheduleSaving(false);
    }
  };

  const formatScheduleInfo = (imp) => {
    if (!imp.schedule_enabled || !imp.cron_expression) return null;
    const parts = imp.cron_expression.trim().split(/\s+/);
    if (parts.length !== 5) return null;
    const [min, hr, , , dow] = parts;
    let freq = 'Daily';
    if (hr.startsWith('*/')) freq = `Every ${hr.substring(2)}h`;
    else if (hr === '*') freq = 'Hourly';
    else if (dow === '1-5') freq = 'Weekdays';
    else if (dow === '0,6' || dow === '6,0') freq = 'Weekends';
    else if (dow !== '*') {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      freq = dow.split(',').map(d => dayNames[parseInt(d)] || d).join(', ');
    }
    let time = '';
    if (hr !== '*' && !hr.startsWith('*/')) {
      const h = parseInt(hr);
      const m = parseInt(min);
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      time = ` at ${h12}:${m.toString().padStart(2, '0')} ${period}`;
    }
    let nextRun = '';
    if (imp.next_scheduled && imp.next_scheduled !== '1970-01-01 00:00:00') {
      const d = new Date(imp.next_scheduled);
      nextRun = ` · Next: ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    }
    return `${freq}${time}${nextRun}`;
  };

  // === Mapping ===
  const openMapping = async (imp) => {
    setMappingImport(imp);
    setMappingOpen(true);
    setMappingLoading(true);
    setMappingSuggestions(null);

    try {
      // Check if metadata already exists
      const metaRes = await googleSheetsService.getMetadata(imp.id);
      if (metaRes.success && metaRes.metadata) {
        setMappingSuggestions({
          table_description: metaRes.metadata.tableDescription,
          data_category: metaRes.metadata.dataCategory,
          suggested_brand_id: metaRes.metadata.suggestedBrandId,
          confirmed: metaRes.metadata.confirmed,
          columns: metaRes.metadata.columns.map(c => ({
            column_name: c.columnName,
            column_description: c.columnDescription,
            column_role: c.columnRole,
            maps_to_table: c.mapsToTable,
            maps_to_column: c.mapsToColumn,
            match_rate: c.matchRate,
            match_note: c.matchNote,
          })),
        });
      } else {
        // Generate new suggestions via LLM
        const res = await googleSheetsService.suggestMapping(imp.id);
        if (res.success) {
          setMappingSuggestions(res.suggestions);
        }
      }
    } catch (err) {
      notify(err.response?.data?.error || 'Failed to generate mapping suggestions', 'error');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleRegenerateSuggestions = async () => {
    setMappingLoading(true);
    try {
      const res = await googleSheetsService.suggestMapping(mappingImport.id);
      if (res.success) {
        setMappingSuggestions(res.suggestions);
      }
    } catch (err) {
      notify('Failed to regenerate suggestions', 'error');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleConfirmMapping = async () => {
    setMappingConfirming(true);
    try {
      await googleSheetsService.confirmMapping(mappingImport.id, mappingSuggestions);
      notify('Column mapping saved');
      setMappingOpen(false);
    } catch (err) {
      notify('Failed to save mapping', 'error');
    } finally {
      setMappingConfirming(false);
    }
  };

  const updateColumnDescription = (colIdx, field, value) => {
    setMappingSuggestions(prev => ({
      ...prev,
      columns: prev.columns.map((c, i) => i === colIdx ? { ...c, [field]: value } : c),
    }));
  };

  // === Dialog helpers ===
  const closeImportDialog = () => {
    setImportOpen(false);
    setImportStep(0);
    setInputMode(0);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSheet(null);
    setTabs([]);
    setSelectedTab(null);
    setSchema(null);
    setTableName('');
    setHeaderRow(1);
  };

  const openImportDialog = () => {
    closeImportDialog();
    setImportOpen(true);
  };

  // === Connect redirect ===
  const handleConnect = () => {
    window.location.href = '/connectors';
  };

  // === Render ===
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1500, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Google Sheets</Typography>
          <Typography variant="body2" color="text.secondary">
            Import Google Sheets into your analytics database
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadImports} size="small">
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={connected ? openImportDialog : handleConnect}
          >
            {connected ? 'Import Sheet' : 'Connect Google'}
          </Button>
        </Box>
      </Box>

      {/* Connection status */}
      {connected === false && (
        <Alert severity="info" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={handleConnect}>Connect</Button>
        }>
          Connect your Google Workspace account to import sheets.
        </Alert>
      )}

      {/* Imports list */}
      {imports.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TableChartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No sheets imported yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Import a Google Sheet to query it alongside your analytics data
          </Typography>
          {connected && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openImportDialog}>
              Import Your First Sheet
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sheet</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell align="right">Cols</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 160 }}>Last Fetched</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {imports.map((imp) => {
                const scheduleInfo = formatScheduleInfo(imp);
                return (
                <TableRow key={imp.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {imp.spreadsheet_name || imp.spreadsheet_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tab: {imp.sheet_name}
                      </Typography>
                      {scheduleInfo && (
                        <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                          <ScheduleIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                          {scheduleInfo}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                      {imp.clickhouse_database}.{imp.clickhouse_table}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={imp.refresh_mode || 'full'}
                      onChange={(e) => handleToggleMode(imp, e.target.value)}
                      disabled={updatingMode === imp.id}
                      sx={{ fontSize: '0.8rem', minWidth: 100, '& .MuiSelect-select': { py: 0.5 } }}
                    >
                      <MenuItem value="full" sx={{ fontSize: '0.8rem' }}>Full</MenuItem>
                      <MenuItem value="additive" sx={{ fontSize: '0.8rem' }}>Additive</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">{imp.row_count?.toLocaleString()}</TableCell>
                  <TableCell align="right">{imp.column_count}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {imp.last_fetched_at && imp.last_fetched_at !== '1970-01-01 00:00:00'
                      ? new Date(imp.last_fetched_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : 'Never'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Schedule">
                      <IconButton
                        size="small"
                        onClick={(e) => openSchedulePopover(e, imp)}
                        color={imp.schedule_enabled ? 'primary' : 'default'}
                      >
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Map columns (AI)">
                      <IconButton size="small" onClick={() => openMapping(imp)} color="primary">
                        <AutoFixHighIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh data">
                      <IconButton
                        size="small"
                        onClick={() => handleRefresh(imp.id)}
                        disabled={refreshingId === imp.id}
                      >
                        {refreshingId === imp.id ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove import">
                      <IconButton size="small" onClick={() => setDeleteConfirm(imp)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ========== IMPORT DIALOG ========== */}
      <Dialog open={importOpen} onClose={closeImportDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {importStep === 0 && 'Select a Google Sheet'}
          {importStep === 1 && `Select Tab — ${selectedSheet?.name}`}
          {importStep === 2 && 'Preview & Import'}
          {importStep === 3 && 'Importing...'}
        </DialogTitle>
        <DialogContent dividers>

          {/* Step 0: Browse / Paste URL */}
          {importStep === 0 && (
            <Box>
              <Tabs value={inputMode} onChange={(_, v) => setInputMode(v)} sx={{ mb: 2 }}>
                <Tab icon={<SearchIcon />} label="Search" iconPosition="start" />
                <Tab icon={<LinkIcon />} label="Paste URL" iconPosition="start" />
              </Tabs>

              {inputMode === 0 && (
                <Box>
                  <TextField
                    fullWidth
                    placeholder="Search your Google Drive for spreadsheets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleSearch} disabled={searching}>
                            {searching ? <CircularProgress size={20} /> : <SearchIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <List>
                    {searchResults.map((file) => (
                      <ListItem
                        key={file.spreadsheetId}
                        button
                        onClick={() => selectSheet(file)}
                        sx={{ borderRadius: 1, mb: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
                      >
                        <ListItemIcon><DescriptionIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={`Last modified: ${new Date(file.modifiedTime).toLocaleDateString()}${file.owner ? ` · ${file.owner}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {inputMode === 1 && (
                <TextField
                  fullWidth
                  placeholder="Paste Google Sheets URL (https://docs.google.com/spreadsheets/d/...)"
                  onChange={(e) => handlePasteUrl(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LinkIcon /></InputAdornment>,
                  }}
                />
              )}
            </Box>
          )}

          {/* Step 1: Select Tab */}
          {importStep === 1 && (
            <Box>
              {loadingTabs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {tabs.map((tab) => (
                    <ListItem
                      key={tab.sheetId}
                      button
                      onClick={() => selectTab(tab)}
                      sx={{ borderRadius: 1, mb: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <ListItemIcon><TableChartIcon /></ListItemIcon>
                      <ListItemText
                        primary={tab.title}
                        secondary={`${tab.rowCount?.toLocaleString()} rows × ${tab.columnCount} columns`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Step 2: Schema Preview */}
          {importStep === 2 && (
            <Box>
              {loadingSchema ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : schema ? (
                <Box>
                  {/* Config */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                      label="Table Name"
                      value={tableName}
                      onChange={(e) => setTableName(e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      helperText="Name of the ClickHouse table"
                    />
                    <TextField
                      label="Header Row"
                      type="number"
                      value={headerRow}
                      onChange={(e) => setHeaderRow(parseInt(e.target.value) || 1)}
                      size="small"
                      sx={{ width: 120 }}
                      inputProps={{ min: 1 }}
                    />
                  </Box>

                  {/* Column list */}
                  <Typography variant="subtitle2" gutterBottom>
                    {schema.columns.length} columns detected · ~{schema.totalRows?.toLocaleString()} data rows
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300, mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Original Header</TableCell>
                          <TableCell>Column Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Sample</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {schema.columns.map((col, i) => (
                          <TableRow key={i}>
                            <TableCell>{col.original}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{col.sanitized}</TableCell>
                            <TableCell><Chip label={col.type} size="small" /></TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {col.sampleValues?.[0] || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Preview rows */}
                  {schema.previewRows?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Data Preview</Typography>
                      <TableContainer sx={{ maxHeight: 200 }}>
                        <Table size="small">
                          <TableBody>
                            {schema.previewRows.map((row, ri) => (
                              <TableRow key={ri}>
                                {row.map((cell, ci) => (
                                  <TableCell key={ci} sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {cell}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>
              ) : null}
            </Box>
          )}

          {/* Step 3: Importing */}
          {importStep === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
              <CircularProgress size={48} />
              <Typography>Importing sheet data...</Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a moment for large sheets
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {importStep > 0 && importStep < 3 && (
            <Button onClick={() => setImportStep(importStep - 1)}>Back</Button>
          )}
          <Button onClick={closeImportDialog}>Cancel</Button>
          {importStep === 2 && schema && (
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleImport}
              disabled={!tableName.trim()}
            >
              Import {schema.totalRows?.toLocaleString()} Rows
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ========== DELETE CONFIRM ========== */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Remove Import?</DialogTitle>
        <DialogContent>
          <Typography>
            This will drop the table <strong>{deleteConfirm?.clickhouse_database}.{deleteConfirm?.clickhouse_table}</strong> and remove the import record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== MAPPING DIALOG ========== */}
      <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon color="primary" />
          Column Mapping — {mappingImport?.clickhouse_table}
        </DialogTitle>
        <DialogContent dividers>
          {mappingLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, gap: 2 }}>
              <CircularProgress size={48} />
              <Typography>AI is analyzing your data...</Typography>
              <Typography variant="body2" color="text.secondary">
                Reading column names, sample values, and suggesting descriptions
              </Typography>
            </Box>
          ) : mappingSuggestions ? (
            <Box>
              {/* Table-level info */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                <TextField
                  fullWidth
                  label="Table Description"
                  value={mappingSuggestions.table_description || ''}
                  onChange={(e) => setMappingSuggestions(prev => ({ ...prev, table_description: e.target.value }))}
                  size="small"
                  sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Data Category</InputLabel>
                    <Select
                      value={mappingSuggestions.data_category || 'other'}
                      label="Data Category"
                      onChange={(e) => setMappingSuggestions(prev => ({ ...prev, data_category: e.target.value }))}
                    >
                      <MenuItem value="fulfillment_costs">Fulfillment Costs</MenuItem>
                      <MenuItem value="marketing_costs">Marketing Costs</MenuItem>
                      <MenuItem value="revenue">Revenue</MenuItem>
                      <MenuItem value="inventory">Inventory</MenuItem>
                      <MenuItem value="customer_data">Customer Data</MenuItem>
                      <MenuItem value="product_data">Product Data</MenuItem>
                      <MenuItem value="financial">Financial</MenuItem>
                      <MenuItem value="operational">Operational</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  {mappingSuggestions.confirmed && (
                    <Chip icon={<CheckCircleIcon />} label="Confirmed" color="success" size="small" />
                  )}
                </Box>
              </Paper>

              {/* Column mappings */}
              <Typography variant="subtitle2" gutterBottom>
                Column Descriptions & Mappings
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Column</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 250 }}>Description (AI suggested)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Maps To</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Match</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappingSuggestions.columns.map((col, idx) => (
                      <TableRow key={col.column_name} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                            {col.column_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={col.column_role || 'metadata'}
                            onChange={(e) => updateColumnDescription(idx, 'column_role', e.target.value)}
                            sx={{ fontSize: '0.8rem', minWidth: 130 }}
                          >
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="identifier">Identifier</MenuItem>
                            <MenuItem value="dimension">Dimension</MenuItem>
                            <MenuItem value="measure_cost">Cost</MenuItem>
                            <MenuItem value="measure_revenue">Revenue</MenuItem>
                            <MenuItem value="measure_quantity">Quantity</MenuItem>
                            <MenuItem value="measure_other">Other Measure</MenuItem>
                            <MenuItem value="metadata">Metadata</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={col.column_description || ''}
                            onChange={(e) => updateColumnDescription(idx, 'column_description', e.target.value)}
                            sx={{ '& input': { fontSize: '0.85rem' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <TextField
                              size="small"
                              placeholder="table"
                              value={col.maps_to_table || ''}
                              onChange={(e) => updateColumnDescription(idx, 'maps_to_table', e.target.value)}
                              sx={{ width: 120, '& input': { fontSize: '0.75rem', fontFamily: 'monospace' } }}
                            />
                            <Typography variant="caption" color="text.disabled">.</Typography>
                            <TextField
                              size="small"
                              placeholder="column"
                              value={col.maps_to_column || ''}
                              onChange={(e) => updateColumnDescription(idx, 'maps_to_column', e.target.value)}
                              sx={{ width: 120, '& input': { fontSize: '0.75rem', fontFamily: 'monospace' } }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {col.match_rate !== undefined && col.match_rate >= 0 ? (
                            <Tooltip title={col.match_note || ''}>
                              <Chip
                                size="small"
                                label={`${col.match_rate}%`}
                                color={col.match_rate >= 70 ? 'success' : col.match_rate >= 30 ? 'warning' : 'error'}
                                variant="outlined"
                                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                              />
                            </Tooltip>
                          ) : col.maps_to_table ? (
                            <Typography variant="caption" color="text.disabled">not verified</Typography>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : (
            <Alert severity="error">Failed to generate suggestions. Try again.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegenerateSuggestions} startIcon={<AutoFixHighIcon />} disabled={mappingLoading}>
            Regenerate
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmMapping}
            disabled={mappingLoading || mappingConfirming || !mappingSuggestions}
            startIcon={mappingConfirming ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {mappingSuggestions?.confirmed ? 'Update Mapping' : 'Confirm Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== SCHEDULE POPOVER ========== */}
      <Popover
        open={!!scheduleAnchor}
        anchorEl={scheduleAnchor}
        onClose={closeSchedulePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2.5, width: 480, maxHeight: 520 } }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Schedule — {scheduleImport?.sheet_name}
        </Typography>
        <CronBuilder value={cronExpression} onChange={setCronExpression} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {scheduleImport?.schedule_enabled ? (
            <Button size="small" color="error" onClick={handleRemoveSchedule} disabled={scheduleSaving}>
              Remove Schedule
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={closeSchedulePopover}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSaveSchedule}
              disabled={scheduleSaving}
              startIcon={scheduleSaving ? <CircularProgress size={14} /> : <ScheduleIcon />}
            >
              {scheduleImport?.schedule_enabled ? 'Update' : 'Enable'}
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification(n => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} onClose={() => setNotification(n => ({ ...n, open: false }))}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
